import React from 'react';
import Box from '@components/Atoms/Box';
import { useUser } from '@reducers/user/hooks';
import ShowIf from '@components/Atoms/ShowIf';
import { BLOOMS_MAP } from '@components/Selectors/BloomsSelector/BloomsSelector';
import {
  useContentFilterContext,
  useContentFilterElement,
  useContentFilterSkillContext,
  useContentFilterStandardContext,
} from '@components/ContentFilters/ContentFilterProvider';
import { SharedAlgoliaProvider } from '@components/Algolia/SharedAlgoliaProvider';
import AlgoliaTable from '@components/AlgoliaIndexTable/Table';
import {
  useSelectedQuestions,
  useAddQuestion,
  useRemoveQuestion,
} from '@components/QuestionSelector/QuestionSelector.provider';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { Divider, Checkbox, FormControlLabel } from '@mui/material';
import { withInteractionContext } from '@components/ElementAnalytics/InteractionContext';
import Typography from '@components/Atoms/Typography';
import ItemBankToggleButtonGroup, { CHALKTALK } from './ItemBankToggleButtonGroup';
import { useShouldShowCoAuthorFeature } from '../hooks';
import GenerateQuestionsButton from './GenerateQuestionsButton';
import FilterChips from './FilterChips';
import PassageFilter from './PassageFilter';

const QuestionSelection = ({ keyId, setOpen }) => {
  const user = useUser();
  const shouldShowCoAuthorFeature = useShouldShowCoAuthorFeature();
  const {
    curriculumSubjects,
    selectedBlooms,
    selectedDok,
    selectedGrade,
    selectedReadability,
    selectedPassage,
    selectedQuestionType,
    setSelectedPassage,
    setSelectedGrade,
    setSelectedReadability,
    isPassageAllowed,
  } = useContentFilterContext();
  const { selectedSkills, getSelectedSkillObjects } = useContentFilterSkillContext();
  const { getSelectedStandardObjects } = useContentFilterStandardContext();
  const contentFilterRef = React.useRef<HTMLDivElement>(null);
  const ContentFilterElement = useContentFilterElement() as React.ElementType;
  const [passageFilterOpen, setPassageFilterOpen] = React.useState(false);

  // Get selections and handlers directly from the provider
  const { selectedQuestions } = useSelectedQuestions();
  const addQuestion = useAddQuestion();
  const removeQuestion = useRemoveQuestion();

  const [itemBankKey, setItemBankKey] = React.useState<any>(CHALKTALK);
  const [showUnaligned, setShowUnaligned] = React.useState(false);

  const subject = curriculumSubjects?.[0]?.subject;

  const getSkillsToFilter = (skills: number[], includeUnaligned: boolean) => {
    // return selected skills if they exist
    if (skills.length) {
      return getSelectedSkillObjects();
    }

    // if no standards or skills selected, return all skills to filter the entire item bank
    // with items only tagged with skills
    return includeUnaligned
      ? [
          {
            id: '',
            image_url: '',
            name: 'Unaligned',
            slug: 'unaligned',
          },
        ]
      : [];
  };

  // Define the question selection handler to connect to the QuestionSelectorProvider
  const handleQuestionSelect = React.useCallback(
    (questionId: string, selected: boolean) => {
      if (selected) {
        addQuestion(questionId);
      } else {
        removeQuestion(questionId);
      }
    },
    [addQuestion, removeQuestion],
  );

  return (
    <SharedAlgoliaProvider indexName="learnosity_items">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'white',
          padding: '1rem',
          mx: '1rem',
        }}
      >
        {/* Filter Row */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            backgroundColor: 'white',
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            {ContentFilterElement && (
              <ContentFilterElement ref={contentFilterRef} enableCounts />
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ShowIf If={shouldShowCoAuthorFeature}>
              <ItemBankToggleButtonGroup setItemBankKey={setItemBankKey} />
            </ShowIf>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: '0.5rem' }}>
          <FormControlLabel
            data-testid="question-selection-show-unaligned-checkbox"
            control={
              <Checkbox
                checked={showUnaligned}
                onClick={() => setShowUnaligned((prev) => !prev)}
                size="small"
                data-testid="show-unaligned-checkbox"
              />
            }
            label='Show questions without "skills"'
          />
        </Box>

        {/* Filter Summary and Passages */}
        <ShowIf If={isPassageAllowed(subject)}>
          <PassageFilter
            open={passageFilterOpen}
            readability={selectedReadability}
            grade={selectedGrade}
            author={itemBankKey === CHALKTALK ? 'chalktalk' : user.id}
            onPassageChange={setSelectedPassage}
            onReadabiltyChange={setSelectedReadability}
            onGradeChange={setSelectedGrade}
          />
        </ShowIf>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'row',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
          }}
        >
          <ShowIf If={isPassageAllowed(subject)}>
            <Divider
              data-testid="question-selection-passage-toggle"
              sx={{
                my: 0,
                cursor: 'pointer',
                flex: '0 0 auto',
              }}
              onClick={() => setPassageFilterOpen((prev) => !prev)}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                Passage Filters
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    transition: 'transform 0.2s ease',
                    transform: passageFilterOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                >
                  <KeyboardArrowDown fontSize="small" />
                </Box>
              </Typography>
            </Divider>
          </ShowIf>
          <Box
            sx={{
              flex: '1 1 auto',
              display: 'flex',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              gap: '0.5rem',
            }}
          >
            <FilterChips />
          </Box>
        </Box>

        {/* TABLE OF QUESTIONS */}
        <AlgoliaTable
          keyId={keyId}
          showCheckbox
          selectedStandards={getSelectedStandardObjects() as any}
          selectedSkills={getSkillsToFilter(selectedSkills, showUnaligned)}
          selectedBloomsTaxonomy={selectedBlooms.map((b) => BLOOMS_MAP[b])}
          selectedDepthOfKnowledge={selectedDok}
          questionSource={itemBankKey}
          emptyStateComponent={
            shouldShowCoAuthorFeature ? (
              <GenerateQuestionsButton
                dataTestId="question-selection-generate-button"
                setOpen={setOpen}
                hasAuthorAideAccess={shouldShowCoAuthorFeature}
              />
            ) : null
          }
          // Pass external selection handlers and state
          externalSelectedQuestions={selectedQuestions}
          onQuestionSelect={handleQuestionSelect}
          selectedPassageReference={selectedPassage?.reference}
          selectedReadability={selectedReadability}
          selectedGrade={selectedGrade}
          selectedQuestionType={selectedQuestionType}
          selectedSubject={subject?.name}
          showUnaligned={showUnaligned}
        />
      </Box>
    </SharedAlgoliaProvider>
  );
};

export default withInteractionContext(QuestionSelection, 'question-selection');
